import { useQuery } from '@apollo/client/react';
import { formatDate } from '@repo/util/formatDate';

import GET_OPT_OUT_ITEMS_Q from '../gql/optOutItems_index.gql';

import { Table } from '@repo/ui/Table';
import { Pagination, decodePaging, type PagingSchema } from '@repo/ui/Pagination';

import { Mercury } from '@repo/types/Models';
import type { UseZodFormReturn } from '@repo/hooks/useZodForm';
import type { UseZodQueryReturn } from '@repo/hooks/useZodQuery';

const PAGE_SIZE = 10;

export interface OptOutsTableProps<T extends PagingSchema> {
  queryList?: Mercury.Queries.QueryList;
  page?: string | undefined;
  filter: UseZodFormReturn<T> | UseZodQueryReturn<T>;
}

export const OptOutsTable = <T extends PagingSchema = PagingSchema>({
  queryList,
  page,
  filter,
}: OptOutsTableProps<T>) => {
  const { loading, error, data } = useQuery(GET_OPT_OUT_ITEMS_Q, {
    variables: {
      ...decodePaging(page, PAGE_SIZE),
      queryList,
    },
    notifyOnNetworkStatusChange: true,
  });

  const pageInfo = data?.messagingOptoutItems?.pageInfo;
  const nodes = data?.messagingOptoutItems?.edges?.map((item) => item.node);

  return (
    <div>
      <Table
        loading={loading}
        pageSize={PAGE_SIZE}
        data={nodes ?? []}
        dataType="opt-out items"
        error={!!error}
        textSm={true}
        columns={[
          {
            title: 'Type',
            width: 'sm',
            Cell({ entry: { type } }) {
              return <span>{type}</span>;
            },
          },
          {
            title: 'Remote Number',
            width: 'md',
            Cell({ entry: { remoteNumber } }) {
              return <span>{remoteNumber ?? '-'}</span>;
            },
          },
          {
            title: 'Host Number',
            width: 'md',
            Cell({ entry: { hostNumber } }) {
              return <span>{hostNumber ?? '-'}</span>;
            },
          },
          {
            title: 'Keyword',
            width: 'sm',
            Cell({ entry: { keyword } }) {
              return <span>{keyword ?? '-'}</span>;
            },
          },
          {
            title: 'TCR Brand',
            width: 'md',
            Cell({ entry: { cspBrand } }) {
              return <span>{cspBrand?.displayName ?? '-'}</span>;
            },
          },
          {
            title: 'TCR Campaign',
            width: 'md',
            Cell({ entry: { cspCampaign } }) {
              return <span>{cspCampaign?.usecase ?? '-'}</span>;
            },
          },
          {
            title: 'Account',
            width: 'md',
            Cell({ entry: { account } }) {
              return <span>{account?.name ?? '-'}</span>;
            },
          },

          {
            title: 'Opt-Out Date',
            width: 'md',
            Cell({ entry: { insertedAt } }) {
              return <span>{formatDate(insertedAt) ?? '-'}</span>;
            },
          },
        ]}
      ></Table>
      {!error && <Pagination {...{ filter, pageInfo }} />}
    </div>
  );
};
